import { blockDateRange, blockLimitedRequestSubjects } from 'venn-state';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { compact, groupBy, isNil, keyBy, sum } from 'lodash';
import type { HoldingPeriod, SimpleFund, TaxStatusEnum } from 'venn-api';
import { useSelectedMetrics } from '../../../../studio-blocks/logic/useSelectedMetrics';
import { useFilteredRequestsAndAnalyses } from '../../../utils/hooks/use-filtered-requests';
import type { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import { BasicHeaderRenderer } from '../../../../studio-blocks/components/grid/renderers/BasicHeaderRenderer';
import { lazyMergeClasses } from '../../../../utils/ag-grid/styling';
import { type CustomizableMetric, mergePortfolios, recursiveGetFunds, traversePortfolio } from 'venn-utils';
import moment from 'moment';
import { baseHeaderClassFn } from '../../../../studio-blocks/logic/columnUtils';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { getDefaultCellClass, getDefaultValueFormat } from '../../../../studio-blocks/logic/gridStyling';
import { usePortfolioMultiFiltering } from '../../../utils/hooks/use-portfolio-multi-filtering';
import { buildTaxImpactTreeRows, type TaxImpactRow } from './buildTaxImpactTree';
import { computeHoldingPeriodLabel } from './computeHoldingPeriodLabel';

const holdingPeriodTypeToLabel: Record<string, string> = {
  SHORT_TERM: 'Short Term',
  LONG_TERM: 'Long Term',
};

const resolveHoldingPeriodLabel = (holdingPeriod: HoldingPeriod, asOfDate: number | undefined): string | undefined => {
  if (holdingPeriod.type !== 'FROM_DATE') {
    return holdingPeriodTypeToLabel[holdingPeriod.type];
  }
  return computeHoldingPeriodLabel(holdingPeriod.datePurchased, asOfDate);
};

const taxStatusToLabel: Record<TaxStatusEnum, string> = {
  TAX_EXEMPT: 'Tax-Exempt',
  TAX_DEFERRED: 'Tax-Deferred',
  TAXABLE: 'Taxable',
};

const REQUIRED_FIELDS = ['marketValue', 'costBasis', 'startingQuantity'];

const getCellRendererParams =
  (metric: CustomizableMetric) =>
  ({ data, value }: ICellRendererParams<TaxImpactRow>) => {
    const shouldShowError =
      data?.rowType === 'MISSING' ||
      (data?.rowType === 'UNEXECUTED' && REQUIRED_FIELDS.includes(metric.key) && isNil(value));
    return {
      errors: shouldShowError
        ? ['Tax information missing. Reupload the portfolio with this information, or add via Portfolio Allocator.']
        : [],
      type: metric.dataType,
    };
  };

export const useTaxImpact = (id: string) => {
  const selectedMetrics = useSelectedMetrics();
  const { analyses } = useFilteredRequestsAndAnalyses(id);
  const subjects = useRecoilValue(blockLimitedRequestSubjects(id));
  const asOfDate = useRecoilValue(blockDateRange(id))?.range.to;
  const { filter } = usePortfolioMultiFiltering(id);

  const fundsMap = useMemo(() => {
    const funds = subjects.map((subject) => subject.portfolio).flatMap((portfolio) => recursiveGetFunds(portfolio));
    return keyBy(funds, 'id');
  }, [subjects]);

  const nodesWithIncompleteLots = useMemo(() => {
    const funds: SimpleFund[] = [];
    for (const node of traversePortfolio(subjects[0]?.portfolio)) {
      if (node.fund && (!node.lots || node.lots.length === 0)) {
        funds.push(node.fund);
      }
    }
    return funds;
  }, [subjects]);

  const { rowData, pinnedBottomRowData, totalImpact } = useMemo(() => {
    const impactAnalysis = analyses[0]?.[0]?.taxImpact[1];
    const transactions =
      impactAnalysis?.ledger.map(({ investmentLot, transaction, estimatedTax, endingQuantity }) => {
        const { marketValue, quantity, metadata } = investmentLot;
        const transactedQuantity =
          transaction.transactionType === 'SELL'
            ? -transaction.absTransactionQuantity
            : transaction.absTransactionQuantity;
        return {
          investmentId: metadata.investmentId,
          label: fundsMap[metadata.investmentId]?.name,
          rowType: 'TRANSACTION' as const,
          purchaseDate:
            metadata.holdingPeriod.type === 'FROM_DATE'
              ? moment.utc(metadata.holdingPeriod.datePurchased).format('DD MMM YYYY')
              : undefined,
          holdingPeriod: resolveHoldingPeriodLabel(metadata.holdingPeriod, asOfDate),
          marketValue,
          costBasis: metadata.unitCostBasis * quantity,
          startingQuantity: quantity,
          transactedQuantity,
          endingQuantity,
          gainLoss: transaction.realizedGainLoss,
          estimatedTax,
          taxStatus: taxStatusToLabel[metadata.taxStatus],
        };
      }) ?? [];
    const unexecuted =
      impactAnalysis?.unexecutedLots.map(({ marketValue, metadata, quantity }) => ({
        investmentId: metadata.investmentId,
        label: fundsMap[metadata.investmentId]?.name,
        rowType: 'UNEXECUTED' as const,
        purchaseDate:
          metadata.holdingPeriod.type === 'FROM_DATE'
            ? moment.utc(metadata.holdingPeriod.datePurchased).format('DD MMM YYYY')
            : undefined,
        holdingPeriod: resolveHoldingPeriodLabel(metadata.holdingPeriod, asOfDate),
        marketValue,
        costBasis: metadata.unitCostBasis * quantity,
        startingQuantity: quantity,
        transactedQuantity: 0,
        endingQuantity: quantity,
        gainLoss: 0,
        estimatedTax: 0,
        taxStatus: taxStatusToLabel[metadata.taxStatus],
      })) ?? [];
    const unfilledLots = Object.entries(impactAnalysis?.unfulfilledPositionSize ?? {}).map(
      ([investmentId, positionSize]) => {
        const { marketValue, quantity } = positionSize as { marketValue: number; quantity: number | null };
        return {
          investmentId,
          label: fundsMap[investmentId]?.name,
          rowType: 'UNFILLED' as const,
          purchaseDate: undefined,
          holdingPeriod: undefined,
          marketValue,
          costBasis: undefined,
          startingQuantity: 0,
          transactedQuantity: quantity ?? undefined,
          endingQuantity: quantity ?? undefined,
          gainLoss: 0,
          estimatedTax: 0,
          taxStatus: undefined,
        };
      },
    );
    const missingLots = nodesWithIncompleteLots.map((fund) => ({
      investmentId: fund.id,
      label: fund.name,
      rowType: 'MISSING' as const,
      purchaseDate: undefined,
      holdingPeriod: undefined,
      marketValue: undefined,
      costBasis: undefined,
      startingQuantity: undefined,
      transactedQuantity: undefined,
      endingQuantity: undefined,
      gainLoss: undefined,
      estimatedTax: undefined,
      taxStatus: undefined,
    }));

    const allLots = [...transactions, ...unexecuted, ...unfilledLots, ...missingLots];

    const lotsByInvestment = groupBy(allLots, 'investmentId');
    const mergedPortfolioChildren = mergePortfolios(compact(subjects.map((s) => s.portfolio)));
    const placedInvestmentIds = new Set<string>();
    const rootUsedNames: Record<string, number> = {};

    const rowData = mergedPortfolioChildren.length
      ? buildTaxImpactTreeRows({
          portfolioChildren: mergedPortfolioChildren,
          parentPath: [],
          depth: 0,
          lotsByInvestment,
          selectedLevels: filter.levels,
          placedInvestmentIds,
          usedNames: rootUsedNames,
        })
      : [];

    const pinnedBottomRowData = [
      {
        label: 'Current Portfolio Total (unfilled lots excluded)',
        isStrategy: false,
        purchaseDate: '',
        holdingPeriod: '',
        marketValue: sum(
          compact([...transactions, ...unexecuted, ...missingLots].map(({ marketValue }) => marketValue)),
        ),
        costBasis: sum(compact(allLots.map(({ costBasis }) => costBasis))),
        startingQuantity: sum(compact(allLots.map(({ startingQuantity }) => startingQuantity))),
        transactedQuantity: sum(compact(allLots.map(({ transactedQuantity }) => transactedQuantity))),
        endingQuantity: sum(compact(allLots.map(({ endingQuantity }) => endingQuantity))),
        gainLoss: sum(compact(allLots.map(({ gainLoss }) => gainLoss))),
        estimatedTax: sum(compact(allLots.map(({ estimatedTax }) => estimatedTax))),
        taxStatus: '',
      },
      {
        label: 'Target Portfolio Total (unfilled lots included)',
        isStrategy: false,
        purchaseDate: '',
        holdingPeriod: '',
        marketValue: sum(compact(allLots.map(({ marketValue }) => marketValue))),
        costBasis: sum(compact(allLots.map(({ costBasis }) => costBasis))),
        startingQuantity: sum(compact(allLots.map(({ startingQuantity }) => startingQuantity))),
        transactedQuantity: sum(compact(allLots.map(({ transactedQuantity }) => transactedQuantity))),
        endingQuantity: sum(compact(allLots.map(({ endingQuantity }) => endingQuantity))),
        gainLoss: sum(compact(allLots.map(({ gainLoss }) => gainLoss))),
        estimatedTax: sum(compact(allLots.map(({ estimatedTax }) => estimatedTax))),
        taxStatus: '',
      },
    ];

    const totalImpact = {
      longTermGainLoss: impactAnalysis?.totalLongTermGainLoss,
      shortTermGainLoss: impactAnalysis?.totalShortTermGainLoss,
      netCapitalGains: impactAnalysis?.totalGainLoss,
      longTermTax: impactAnalysis?.totalLongTermTax,
      shortTermTax: impactAnalysis?.totalShortTermTax,
      netTax: impactAnalysis?.totalEstimatedTax,
    };
    return { rowData, pinnedBottomRowData, totalImpact };
  }, [analyses, subjects, filter.levels, fundsMap, nodesWithIncompleteLots, asOfDate]);

  const columnDefs = useMemo((): (ColDef | ColGroupDef)[] => {
    const subjectsHeader = subjects.length === 1 ? subjects[0]?.name : `${subjects[0]?.name} -> ${subjects[1]?.name}`;
    return [
      {
        headerName: subjectsHeader,
        headerGroupComponent: BasicHeaderRenderer,
        headerGroupComponentParams: {
          displayName: subjectsHeader,
          color: 'black',
          style: { textAlign: 'right', width: '100%', color: 'black' },
        },
        headerClass: lazyMergeClasses(baseHeaderClassFn),
        children: selectedMetrics.map(
          (metric) =>
            ({
              field: metric.key,
              headerName: metric.label,
              headerComponent: BasicHeaderRenderer,
              headerComponentParams: { color: 'black' },
              cellRenderer: ValueCellRenderer,
              cellRendererParams: getCellRendererParams(metric),
              cellClass: (params) => getDefaultCellClass(params),
              valueFormatter: getDefaultValueFormat,
              ...(metric.key === 'holdingPeriod' ? { suppressSizeToFit: true } : {}),
            }) as ColDef,
        ),
      },
    ];
  }, [selectedMetrics, subjects]);

  return { columnDefs, rowData, pinnedBottomRowData, totalImpact };
};
